/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */
"use client";

import {
  FeaturePageTemplate,
  type FeaturePageConfig,
} from "@/components/landing/feature-page-template";
import {
  Sparkles,
  Calculator,
  MessageSquare,
  Ruler,
  BookOpen,
  BarChart3,
  Receipt,
  Layers,
} from "lucide-react";

const config: FeaturePageConfig = {
  badge: "AI Built for General Contractors",
  title: "AI for general contractors",
  titleHighlight: "from pre-con to punch list.",
  description: `One platform where AI helps general contractors bid faster, match costs to real supplier rates, audit scope before proposals ship, and run projects from one database. Built for 3–25 person teams. Grounded in your real business data — not generic construction averages.`,

  featureGrid: {
    title: "Where AI shows up for general contractors",
    items: [
      {
        icon: Sparkles,
        title: "Bid management",
        description:
          "Track every bid, every revision, every client decision — all in one pipeline.",
      },
      {
        icon: Sparkles,
        title: "Scope review",
        description:
          "AI audits scope for gaps and missing line items before a bid ships to the client.",
      },
      {
        icon: Sparkles,
        title: "Sub bid matching",
        description:
          "Match subcontractor bids against your baseline estimates with variance flags.",
      },
      {
        icon: Sparkles,
        title: "Multi-trade cost database",
        description:
          "Landscaping, concrete, HVAC, electrical, plumbing, roofing — all in one platform.",
      },
      {
        icon: Sparkles,
        title: "Project lifecycle",
        description:
          "From accepted proposal to budget to invoice — nothing double-entered.",
      },
      {
        icon: Sparkles,
        title: "Hank for GCs",
        description:
          "Ask about any job, sub, margin, or deadline in plain English.",
      },
    ],
  },

  workflow: {
    title: "How it works for general contractors",
    description:
      "From first bid to final invoice — every step in one database, every AI decision reviewable.",
    steps: [
      {
        label: "Bid",
        title: "Pre-con done right",
        description:
          "Upload the RFP, organize the drawings, assign scope to divisions or subs, and build the baseline estimate fast. AI matches material and labor lines to your cost database and flags gaps before the bid goes out.",
        details: [],
      },
      {
        label: "Win",
        title: "Proposals that close",
        description:
          "Turn the estimate into a branded proposal, send it via email or client portal, and track every approval and revision. Nothing falls through the cracks when you're juggling 12 active bids.",
        details: [],
      },
      {
        label: "Run",
        title: "Field to finance, one system",
        description:
          "Accepted proposal becomes an active project with tasks, budgets, subs, and calendar events. Hank answers 'what's over budget?' and 'which job needs my attention tomorrow?' in plain English.",
        details: [],
      },
    ],
  },

  capabilities: [
    {
      icon: Sparkles,
      title: "Works across every trade you touch",
      description:
        "GCs juggle subs from landscaping to HVAC. Contractor Co-Pilot ships with cost databases for all 8 supported trades so you can bid and manage work across any of them in one workspace. No per-trade tool sprawl.",
      bullets: [
        "8 pre-loaded trade cost databases",
        "Unified bid and estimate workflow across trades",
        "Division-level budget tracking",
        "Single audit trail across every job",
      ],
    },
    {
      icon: Sparkles,
      title: "AI scope and margin audits before bids ship",
      description:
        "Hank reads your estimate, compares it against similar past jobs, and flags anything that looks off — missing labor lines, unusual margins, scope gaps. Catch the $5k omission before the client does.",
      bullets: [
        "Margin check against your targets",
        "Missing-line detection across work areas",
        "Comparable job lookup from your history",
        "Plain-English audit summaries",
      ],
    },
    {
      icon: Sparkles,
      title: "Sub bid management that actually scales",
      description:
        "Track sub bids against your baseline, compare apples to apples on scope, and flag variance. Know when a sub is low-bidding a gap they'll change-order later.",
      bullets: [
        "Sub bid tracking per scope area",
        "Variance flags against baseline",
        "Scope comparison across subs",
        "Change order early warning",
      ],
    },
    {
      icon: Sparkles,
      title: "One platform from RFP to closeout",
      description:
        "Contractor Co-Pilot connects estimates, proposals, projects, budgets, and invoices — so you don't re-enter data three times between bid and collection.",
      bullets: [
        "Estimate → Proposal → Invoice pipeline",
        "Budget vs. actual tracking",
        "Project timelines with tasks",
        "Document storage per project",
      ],
    },
  ],

  relatedFeatures: [
    {
      label: "General Contracting Solutions",
      href: "/solutions/general-contracting",
      icon: Ruler,
    },
    {
      label: "Profit Margin Calculator",
      href: "/tools/margin-calculator",
      icon: Calculator,
    },
    {
      label: "Construction Estimating Mistakes",
      href: "/blog/construction-estimating-mistakes",
      icon: BookOpen,
    },
    { label: "CC vs STACK", href: "/compare/stack", icon: BarChart3 },
    {
      label: "Construction Estimating",
      href: "/features/estimates",
      icon: Receipt,
    },
    {
      label: "Multi-Division Management",
      href: "/features/divisions",
      icon: Layers,
    },
    {
      label: "AI Assistant (Hank)",
      href: "/features/ai-assistant",
      icon: MessageSquare,
    },
    { label: "All Features", href: "/features", icon: Sparkles },
  ],
};

export function AiForGeneralContractorsPageClient() {
  return <FeaturePageTemplate config={config} />;
}
